import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import { Link } from 'react-router-dom'
import { useIntl } from 'react-intl'
import { useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { Grid } from '@mui/material'

import InputField from 'components/form/InputField'
import DateTimePicker from 'components/form/DateTimePicker'
import ClubSelect from 'components/form/ClubSelect'
import GenericSelect from 'components/form/GenericSelect'

// import SubmitButton from 'components/form/SubmitButton'
import SwitchToggle from 'components/form/SwitchToggle'

import LoadingSpinnerGreen from 'components/ui/LoadingSpinnerGreen'

import { selectClubs } from 'store/clubs/slice'
import { selectGame, clearGame } from 'store/game/slice'
import { getGame, getGameFormats, updateGame } from 'store/game/thunks'
import { selectRoute } from 'store/route/slice'
import { formatDate } from 'utils/formatDate'

interface ParamTypes extends ParamTypesBase {
  id: string
}

export default function GameSettings(): JSX.Element {
  const { id: paramId } = useParams<ParamTypes>()
  const id = paramId || ''
  const [currentId, setCurrentId] = useState<string>('')
  const [courseCountry, setCourseCountry] = useState<number>(0)
  const [gameRound, setGameRound] = useState<GameRound | null>(null)
  const [gameGroup, setGameGroup] = useState<GameGroup | null>(null)
  const [gameCourse, setGameCourse] = useState<GameCourse | null>(null)
  const [gameClub, setGameClub] = useState<Club | null>(null)
  const [courseTeeBoxes, setCourseTeeBoxes] = useState<GameCourseTeeBox[]>([])

  const [clubCourseOptions, setClubCourseOptions] = useState<SelectOption[]>([])
  const [gameFormatOptions, setGameFormatOptions] = useState<SelectOption[]>([])
  const [initialClub, setInitialClub] = useState<SelectOption | null>(null)
  const intl = useIntl()
  const dispatch = useDispatch()
  const { status, game, /*updatingStatus,*/ gameFormats } = useSelector(selectGame)
  const { clubs } = useSelector(selectClubs)
  const route = useSelector(selectRoute)

  const { handleSubmit, control, register, reset, watch } = useForm<GameSettingsForm>()
  const selectedClub = watch('club')

  useEffect(() => {
    if ((id && status === 'idle') || currentId !== id) {
      setCurrentId(id)
      dispatch(getGame(parseInt(id)))
    }
  }, [id, currentId, status, dispatch])

  // Populate data to form fields
  useEffect(() => {
    if (game?.id && status === 'succeeded') {
      const round: GameRound | null = game.rounds.length > 0 ? game.rounds[0] : null
      const group: GameGroup | null = game.groups.length > 0 ? game.groups[0] : null
      const course: GameCourse | null = game.courses.length > 0 ? game.courses[0] : null

      setGameRound(round)
      setGameGroup(group)
      setGameCourse(course)
      setCourseCountry(course?.countryId || 0)
      setCourseTeeBoxes(course?.teeboxes || [])

      const roundDate = round?.startTime?.date || round?.date?.date
      const date = new Date(roundDate)

      const closestToPin = round?.contests.closestToPin
        ? round?.contests.closestToPin.filter((hole) => hole > 0).map((hole) => hole.toString())
        : []
      const longestDrive = round?.contests.longestDrive
        ? round?.contests.longestDrive.filter((hole) => hole > 0).map((hole) => hole.toString())
        : []
      const straightestDrive = round?.contests.straightestDrive
        ? round?.contests.straightestDrive.filter((hole) => hole > 0).map((hole) => hole.toString())
        : []

      const initialData: GameSettingsForm = {
        gameName: game.name,
        gameDate: formatDate(date),
        club: undefined,
        course: course?.id,
        playedHoles: round?.genericConfig.holesToPlay || undefined,
        startHole: group?.startHole,
        primaryGame: round?.primaryGame,
        primaryGameUseHandicaps: round?.primaryConfig.useHandicaps || false,
        primaryGameUsePlayingHandicaps: round?.primaryConfig.usePlayingHandicaps || false,
        primaryGameHandicapPercentage: round?.primaryConfig?.handicapPercentage || 100,
        primaryGameSkinsValue: round?.primaryConfig.skinsWagerValue || undefined,
        primaryGameSkinsUseCarryOver: round?.primaryConfig.skinsUseCarryOver || false,
        primaryTieBreakRule: round?.primaryConfig.tieBreakRule || undefined,
        sideGame: round?.secondaryGame,
        sideGameUseHandicaps: round?.secondaryConfig.useHandicaps || false,
        sideGameUsePlayingHandicaps: round?.secondaryConfig.usePlayingHandicaps || false,
        sideGameHandicapPercentage: round?.secondaryConfig?.handicapPercentage || 100,
        sideGameSkinsValue: round?.secondaryConfig.skinsWagerValue || undefined,
        sideGameSkinsUseCarryOver: round?.secondaryConfig.skinsUseCarryOver || false,
        sideTieBreakRule: round?.secondaryConfig.tieBreakRule || undefined,
        closestToPin: closestToPin,
        longestDrive: longestDrive,
        straightestDrive: straightestDrive,
        measureResultsLongestDrive: round?.contests.measureResultsLongestDrive || false,
        measureResultsStraightestDrive: round?.contests.measureResultsStraightestDrive || false,
        hasContests: closestToPin.length > 0 || longestDrive.length > 0 || straightestDrive.length > 0,
        defaultTeeBoxMen: round?.defaultTeeboxId.men,
        defaultTeeBoxWomen: round?.defaultTeeboxId.women,
      }
      reset(initialData)
    }
  }, [game, status, reset])

  // Get initial club values to club select
  useEffect(() => {
    if (game?.id && clubs && !gameClub) {
      const club = clubs.filter((club: Club) => club.name === gameCourse?.clubName)[0]

      if (club) {
        setGameClub(club)
        setInitialClub({
          key: club.id,
          label: club.name,
        })
      }
    }
  }, [clubs, game, gameClub, gameCourse])

  // Get course select options
  useEffect(() => {
    const noCourseOptions = gameClub && clubCourseOptions.length === 0
    const clubChanged = gameClub && selectedClub && gameClub.id !== selectedClub.key
    if (noCourseOptions || clubChanged) {
      let clubName = gameCourse?.clubName
      if (selectedClub) {
        clubName = selectedClub.label
      }

      const club = clubs.filter((club: Club) => club.name === clubName)[0]

      if (club) {
        setGameClub(club)
        const courseOptions = club.courses.map((course: Course) => {
          return {
            key: course.id,
            label: course.name,
          }
        })

        setClubCourseOptions(courseOptions)
      }
    }
  }, [gameClub, selectedClub, clubCourseOptions, clubs, gameCourse])

  // Get game format options
  useEffect(() => {
    if (gameFormats.length === 0) {
      dispatch(getGameFormats())
    } else {
      const formatOptions = gameFormats.map((gameFormat: GameFormat) => {
        return {
          key: gameFormat.id,
          label: gameFormat.name || '',
        }
      })
      setGameFormatOptions(formatOptions)
    }
  }, [gameFormats, dispatch])

  // Clean up game from store at unmount
  useEffect(() => {
    return () => {
      dispatch(clearGame())
    }
  }, [dispatch])

  const getPercentageOptions = () => {
    const options: SelectOption[] = []

    for (let i = 100; i > 0; i--) {
      options.push({
        key: i,
        label: i.toString(),
      })
    }
    return options
  }

  const getPlayedHolesOptions = () => {
    const options: SelectOption[] = [
      {
        key: '18',
        label: intl.formatMessage({ id: 'strings.full18' }),
      },
      {
        key: 'f9',
        label: intl.formatMessage({ id: 'strings.front9' }),
      },
      {
        key: 'b9',
        label: intl.formatMessage({ id: 'strings.back9' }),
      },
    ]
    return options
  }

  const getStartHoleOptions = () => {
    const options: SelectOption[] = []

    for (let i = 1; i <= 18; i++) {
      options.push({
        key: i,
        label: i.toString(),
      })
    }
    return options
  }

  const getTeeBoxOptions = () => {
    return courseTeeBoxes.map((tb: GameCourseTeeBox) => {
      return { key: tb.id, label: tb.teeboxName }
    })
  }

  const getCoursePar3Holes = () => {
    const holes = gameCourse?.holes || null
    if (holes && Array.isArray(holes)) {
      return holes
        .filter((hole: CourseHole) => hole.par === 3)
        .map((hole: CourseHole) => hole.holeNumber)
        .flat()
    }
    return []
  }

  const getCourseHolesExceptPar3 = () => {
    const holes = gameCourse?.holes || null
    if (holes && Array.isArray(holes)) {
      return holes
        .filter((hole: CourseHole) => hole.par !== 3)
        .map((hole: CourseHole) => hole.holeNumber)
        .flat()
    }
    return []
  }

  const gameHasContests = () => {
    return watch('hasContests') === true
  }

  const submitForm = (data: GameSettingsForm) => {
    if (game?.id) {
      const hasContests = gameHasContests()

      const measureResultsLongestDrive = data.measureResultsLongestDrive === true
      const measureResultsStraightestDrive = data.measureResultsStraightestDrive === true

      const updatedData: GamePayloadData = {
        name: data.gameName,
        rounds: [
          {
            id: gameRound?.id || '',
            date: data.gameDate,
            courseId: data.course || 0,
            holes: data.playedHoles === 18 ? 18 : 9,
            primaryGame: data.primaryGame || 0,
            secondaryGame: data.sideGame || 0,
            defaultTeeboxId: {
              men: data.defaultTeeBoxMen || 0,
              women: data.defaultTeeBoxWomen || 0,
            },
            primaryConfig: {
              useHandicaps: data.primaryGameUseHandicaps,
              usePlayingHandicaps: data.primaryGameUsePlayingHandicaps,
              handicapPercentage: data.primaryGameHandicapPercentage || null,
              modifier: 'modified',
              skinsWagerValue: data.primaryGameSkinsValue || null,
              skinsUseCarryOver: data.primaryGameSkinsUseCarryOver,
              tieBreakRule: data.primaryTieBreakRule || null,
            },
            secondaryConfig: {
              useHandicaps: data.sideGameUseHandicaps,
              usePlayingHandicaps: data.sideGameUsePlayingHandicaps,
              handicapPercentage: data.sideGameHandicapPercentage,
              skinsWagerValue: data.sideGameSkinsValue,
              skinsUseCarryOver: data.sideGameSkinsUseCarryOver,
              tieBreakRule: data.sideTieBreakRule,
            },
            genericConfig: {
              holesToPlay: data.playedHoles ? String(data.playedHoles) : '18',
            },
            contests: {
              closestToPin: hasContests ? data.closestToPin.map((hole) => parseInt(hole)) : [],
              longestDrive: hasContests ? data.longestDrive.map((hole) => parseInt(hole)) : [],
              straightestDrive: hasContests ? data.straightestDrive.map((hole) => parseInt(hole)) : [],
              measureResultsLongestDrive: hasContests ? measureResultsLongestDrive : false,
              measureResultsStraightestDrive: hasContests ? measureResultsStraightestDrive : false,
            },
          },
        ],
        groups: [
          {
            id: gameGroup?.id,
            startHole: data.startHole,
          },
        ],
      }

      const payload: GamePayload = {
        id: Number(game.id),
        successMessage: intl.formatMessage({ id: 'strings.gameUpdatedSuccessfully' }),
        data: updatedData,
      }
      dispatch(updateGame(payload))
    }
  }

  const skinValueOptions: SelectOption[] = [
    { key: 1, label: '1' },
    { key: 2, label: '2' },
    { key: 6, label: '3' },
    { key: 7, label: '4' },
    { key: 3, label: '5' },
    { key: 4, label: '10' },
    { key: 8, label: '20' },
    { key: 9, label: '50' },
    { key: 10, label: '100' },
    { key: 11, label: '500' },
    { key: 12, label: '1000' },
  ]

  const tieBreakOptions: SelectOption[] = [
    { key: 'handicap', label: intl.formatMessage({ id: 'strings.handicap' }) },
    { key: 'countback', label: intl.formatMessage({ id: 'strings.countback' }) },
  ]

  const isSkinsGame = (gameFormatId: number | undefined): boolean => {
    if (!gameFormatId) {
      return false
    }
    const gameFormats = gameFormatOptions.filter((option: SelectOption) => option.key === gameFormatId)
    return gameFormats.length > 0 && gameFormats[0].label === 'Skins'
  }

  if (['idle', 'loading'].includes(status)) {
    return (
      <SpinnerContainer>
        <LoadingSpinnerGreen size={72} />
      </SpinnerContainer>
    )
  }

  return (
    <PageContainer>
      <form onSubmit={handleSubmit(submitForm)}>
        <PageSection>
          <BackLinkContainer>
            <Link to={route.previousUri}>{intl.formatMessage({ id: 'strings.backToGamesList' })}</Link>
          </BackLinkContainer>
          <SectionTitle>{intl.formatMessage({ id: 'strings.general' })}</SectionTitle>
          <InputRow container spacing={3}>
            <Grid item xs={5}>
              <InputField
                register={register}
                name="gameName"
                type="text"
                label={intl.formatMessage({ id: 'strings.gameName' })}
                outlined
              />
            </Grid>
            <Grid item xs={2}>
              <StyledDateTimePicker
                register={register}
                name="gameDate"
                label={intl.formatMessage({ id: 'strings.gameDate' })}
                dateOnly
              />
            </Grid>
          </InputRow>
          <Spacer />
          <InputRow container spacing={3}>
            <Grid item xs={4}>
              <ClubSelect
                name="club"
                control={control}
                initialValue={initialClub}
                initialClubName={game?.courses[0]?.clubName}
                countryId={courseCountry}
                label={intl.formatMessage({ id: 'strings.club' })}
              />
            </Grid>
            <Grid item xs={4}>
              <GenericSelect
                name="course"
                label={intl.formatMessage({ id: 'strings.course' })}
                control={control}
                options={clubCourseOptions}
                initialValue={watch('course')}
              />
            </Grid>
          </InputRow>
          <Spacer />
          <InputRow container spacing={3}>
            <Grid item xs={2}>
              <GenericSelect
                name="playedHoles"
                label={intl.formatMessage({ id: 'strings.playedHoles' })}
                control={control}
                options={getPlayedHolesOptions()}
                initialValue={watch('playedHoles')}
              />
            </Grid>
            <Grid item xs={2}>
              <GenericSelect
                name="startHole"
                label={intl.formatMessage({ id: 'strings.defaultStartingHole' })}
                control={control}
                options={getStartHoleOptions()}
                initialValue={watch('startHole')}
              />
            </Grid>
            <Grid item xs={2}>
              <GenericSelect
                name="defaultTeeBoxMen"
                label={intl.formatMessage({ id: 'strings.defaultTeeBoxMen' })}
                control={control}
                options={getTeeBoxOptions()}
                initialValue={watch('defaultTeeBoxMen')}
              />
            </Grid>
            <Grid item xs={2}>
              <GenericSelect
                name="defaultTeeBoxWomen"
                label={intl.formatMessage({ id: 'strings.defaultTeeBoxWomen' })}
                control={control}
                options={getTeeBoxOptions()}
                initialValue={watch('defaultTeeBoxWomen')}
              />
            </Grid>
          </InputRow>
        </PageSection>

        <PageSection>
          <SectionTitle>{intl.formatMessage({ id: 'strings.gameFormats' })}</SectionTitle>
          <InputRow container spacing={3}>
            <PrimaryGameFormat item xs={5}>
              <GameFormatTitle>{intl.formatMessage({ id: 'strings.primaryGame' })}</GameFormatTitle>
              <GenericSelect
                name="primaryGame"
                label={intl.formatMessage({ id: 'strings.gameFormat' })}
                control={control}
                options={gameFormatOptions.filter((option: SelectOption) => Number(option.key) > 1)}
                initialValue={watch('primaryGame')}
              />
              <LineBreak />
              <SwitchToggle
                control={control}
                name="primaryGameUseHandicaps"
                label={intl.formatMessage({ id: 'strings.useHandicaps' })}
                value={watch('primaryGameUseHandicaps')}
              />
              <LineBreak />
              <SwitchToggle
                control={control}
                name="primaryGameUsePlayingHandicaps"
                label={intl.formatMessage({ id: 'strings.useCourseHandicaps' })}
                value={watch('primaryGameUsePlayingHandicaps')}
              />
              <PercentageSelect
                name="primaryGameHandicapPercentage"
                label={intl.formatMessage({ id: 'strings.percentage' })}
                control={control}
                options={getPercentageOptions()}
                initialValue={watch('primaryGameHandicapPercentage')}
                fullWidth={false}
              />
              <LineBreak />
              <TieBreakSelect
                name="primaryTieBreakRule"
                label={intl.formatMessage({ id: 'strings.tieBreakMethod' })}
                control={control}
                options={tieBreakOptions}
                initialValue={watch('primaryTieBreakRule')}
              />

              {isSkinsGame(watch('primaryGame')) && (
                <SkinsExtraContainer>
                  <SwitchToggle
                    control={control}
                    name="primaryGameSkinsUseCarryOver"
                    label={intl.formatMessage({ id: 'strings.useCarryOver' })}
                    value={watch('primaryGameSkinsUseCarryOver')}
                  />
                  <SkinValueSelect
                    name="primaryGameSkinsValue"
                    label={intl.formatMessage({ id: 'strings.skinValue' })}
                    control={control}
                    options={skinValueOptions}
                    initialValue={watch('primaryGameSkinsValue')}
                  />
                </SkinsExtraContainer>
              )}
            </PrimaryGameFormat>
            <SideGameFormat item xs={5}>
              <GameFormatTitle>{intl.formatMessage({ id: 'strings.sideGame' })}</GameFormatTitle>
              <GenericSelect
                name="sideGame"
                label={intl.formatMessage({ id: 'strings.gameFormat' })}
                control={control}
                options={gameFormatOptions}
                initialValue={watch('sideGame')}
              />
              <LineBreak />
              <SwitchToggle
                control={control}
                name="sideGameUseHandicaps"
                label={intl.formatMessage({ id: 'strings.useHandicaps' })}
                value={watch('sideGameUseHandicaps')}
              />
              <LineBreak />
              <SwitchToggle
                control={control}
                name="sideGameUsePlayingHandicaps"
                label={intl.formatMessage({ id: 'strings.useCourseHandicaps' })}
                value={watch('sideGameUsePlayingHandicaps')}
              />
              <PercentageSelect
                name="sideGameHandicapPercentage"
                label={intl.formatMessage({ id: 'strings.percentage' })}
                control={control}
                options={getPercentageOptions()}
                initialValue={watch('sideGameHandicapPercentage')}
                fullWidth={false}
              />

              <LineBreak />
              <TieBreakSelect
                name="sideTieBreakRule"
                label={intl.formatMessage({ id: 'strings.tieBreakMethod' })}
                control={control}
                options={tieBreakOptions}
                initialValue={watch('sideTieBreakRule')}
              />
              {isSkinsGame(watch('sideGame')) && (
                <SkinsExtraContainer>
                  <SwitchToggle
                    control={control}
                    name="sideGameSkinsUseCarryOver"
                    label={intl.formatMessage({ id: 'strings.useCarryOver' })}
                    value={watch('sideGameSkinsUseCarryOver')}
                  />
                  <SkinValueSelect
                    name="sideGameSkinsValue"
                    label={intl.formatMessage({ id: 'strings.skinValue' })}
                    control={control}
                    options={skinValueOptions}
                    initialValue={watch('sideGameSkinsValue')}
                  />
                </SkinsExtraContainer>
              )}
            </SideGameFormat>
          </InputRow>

          <Spacer />
          <DividerLine />
          <Spacer />

          <InlineSectionTitle>{intl.formatMessage({ id: 'strings.contests' })}</InlineSectionTitle>
          <SwitchToggle control={control} name="hasContests" value={watch('hasContests')} />

          {watch('hasContests') && (
            <InputRow container spacing={3}>
              <Grid item xs={2}>
                <ContestGroupTitle>{intl.formatMessage({ id: 'strings.closestToPin' })}</ContestGroupTitle>
                <CheckboxGroupContainer>
                  {getCoursePar3Holes().map((holeNumber: number, key) => {
                    return (
                      <CheckboxContainer key={key}>
                        <input type="checkbox" {...(register ? register('closestToPin') : {})} value={holeNumber} />
                        <span>{holeNumber}</span>
                      </CheckboxContainer>
                    )
                  })}
                </CheckboxGroupContainer>
              </Grid>
              <Grid item xs={2}>
                <ContestGroupTitle>{intl.formatMessage({ id: 'strings.longestDrive' })}</ContestGroupTitle>
                <CheckboxGroupContainer>
                  {getCourseHolesExceptPar3().map((holeNumber: number, key: number) => {
                    return (
                      <CheckboxContainer key={key}>
                        <input type="checkbox" {...(register ? register('longestDrive') : {})} value={holeNumber} />
                        <span>{holeNumber}</span>
                      </CheckboxContainer>
                    )
                  })}
                </CheckboxGroupContainer>
                <SwitchToggle
                  control={control}
                  name="measureResultsLongestDrive"
                  label={intl.formatMessage({ id: 'strings.measureResults' })}
                  value={watch('measureResultsLongestDrive')}
                />
              </Grid>
              <Grid item xs={2}>
                <ContestGroupTitle>{intl.formatMessage({ id: 'strings.straightestDrive' })}</ContestGroupTitle>
                <CheckboxGroupContainer>
                  {getCourseHolesExceptPar3().map((holeNumber: number, key: number) => {
                    return (
                      <CheckboxContainer key={key}>
                        <input type="checkbox" {...(register ? register('straightestDrive') : {})} value={holeNumber} />
                        <span>{holeNumber}</span>
                      </CheckboxContainer>
                    )
                  })}
                </CheckboxGroupContainer>
                <SwitchToggle
                  control={control}
                  name="measureResultsStraightestDrive"
                  label={intl.formatMessage({ id: 'strings.measureResults' })}
                  value={watch('measureResultsStraightestDrive')}
                />
              </Grid>
            </InputRow>
          )}
        </PageSection>

        {/* <PageSection>
          <SubmitButton status={updatingStatus} text={intl.formatMessage({ id: 'strings.save' })} />
        </PageSection> */}
      </form>
    </PageContainer>
  )
}

const SpinnerContainer = styled.div`
  display: flex;
  width: 100%;
  margin: 50px auto;
  justify-content: center;
`

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding: 10px 10px 20px;
  width: 100%;
`

const PageSection = styled.div`
  background-color: ${(props) => props.theme.customPalette.light};
  padding: 20px 20px 40px 20px;
  margin-bottom: 20px;

  & a {
    color: ${(props) => props.theme.palette.primary.main};
    text-decoration: none;
  }
`

const BackLinkContainer = styled.div`
  margin-bottom: 25px;
`

const SectionTitle = styled.h2`
  font-style: italic;
  font-weight: 800;
  font-size: 20px;
  line-height: 27px;
  color: ${(props) => props.theme.customPalette.darkGray2};
  margin-bottom: 25px;
`

const InlineSectionTitle = styled(SectionTitle)`
  display: inline-block;
  margin-right: 20px;
`

const Spacer = styled.div`
  width: 100%;
  height: 40px;
`

const DividerLine = styled.div`
  width: 80%;
  height: 1px;
  background-color: ${(props) => props.theme.palette.primary.main};
`

const InputRow = styled(Grid)``

const GameFormatTitle = styled.h3`
  font-style: normal;
  font-weight: bold;
  font-size: 18px;
  line-height: 21px;
  color: ${(props) => props.theme.customPalette.darkGray2};
  margin-bottom: 25px;
`

const GameFormat = styled(Grid)`
  & > div:first-of-type {
    width: 50%;
    margin: 20px 0;
  }
`

const PrimaryGameFormat = styled(GameFormat)`
  border-right: 1px solid;
  border-color: ${(props) => props.theme.palette.primary.main};
`

const SideGameFormat = styled(GameFormat)`
  margin-left: 50px;
`

const LineBreak = styled.div`
  flex-basis: 100%;
  height: 0;
`

const PercentageSelect = styled(GenericSelect)`
  margin-top: -20px;
  margin-left: 20px;
  min-width: 100px;
`

const ContestGroupTitle = styled.div`
  color: ${(props) => props.theme.palette.primary.main};
  font-size: 0.75rem;
`

const CheckboxGroupContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-start;
`

const CheckboxContainer = styled.label`
  display: flex;
  width: 20%;
  cursor: pointer;
  font-size: 16px;
  line-height: 27px;
  margin: 10px 0;
  text-align: center;

  & input {
    position: absolute;
    opacity: 0;
    cursor: pointer;
    height: 0;
    width: 0;
  }

  & span {
    height: 27px;
    width: 25px;
    border: 1px solid #939393;
    border-radius: 4px;
    color: ${(props) => props.theme.customPalette.darkGray2};
  }

  &:hover input ~ span {
    background-color: ${(props) => props.theme.palette.primary.main};
    color: #fff;
  }

  & input:checked ~ span {
    background-color: ${(props) => props.theme.palette.primary.main};
    color: #fff;
  }
`

const SkinsExtraContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: ${(props) => props.theme.customPalette.lightGray};
  padding: 20px;
  border-radius: 5px;
  margin: 10px 0;
`

const SkinValueSelect = styled(GenericSelect)`
  background-color: ${(props) => props.theme.customPalette.light};
  width: 150px;
`

const TieBreakSelect = styled(GenericSelect)`
  margin: 40px 0 20px;
  width: 50%;
`

const StyledDateTimePicker = styled(DateTimePicker)``
